import type {
  AiFlashNewsDiscount,
  AiFlashNewsEvent,
  AiFlashNewsReview,
  AiFlashNewsTag,
  AiProposalAction,
  AiProposalDecision,
  AiProposalPreview,
  AiProposalResponse,
  AiProposalSelection,
  AiProposalSource,
  Facets,
  FeedEvent,
  FeedResponse,
  SourceHealth,
  SourceSummary,
} from "./types";
import type { DisplayLocale } from "./locale";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:8787";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item : isRecord(item) ? asString(item.name) : ""))
    .filter(Boolean);
}

function normalizeSources(value: unknown): SourceSummary[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((source) => {
    if (typeof source === "string") return [{ id: source, name: source }];
    if (!isRecord(source)) return [];
    const name = asString(source.name ?? source.source_name);
    if (!name) return [];
    return [{
      id: asString(source.id ?? source.source_id, name),
      name,
      url: asNullableString(source.url ?? source.canonical_url ?? source.primary_url),
      kind: asNullableString(source.kind ?? source.source_type),
    }];
  });
}

function normalizeEvent(value: unknown): FeedEvent | null {
  if (!isRecord(value)) return null;
  const localization = isRecord(value.localization) ? value.localization : {};
  const id = asString(value.id ?? value.event_id);
  const title = asString(value.title ?? value.localized_title ?? localization.title);
  if (!id || !title) return null;

  const publishedRaw = value.publishedAt ?? value.published_at ?? value.occurred_at ?? value.first_seen_at;
  const publishedAt = typeof publishedRaw === "string" ? Date.parse(publishedRaw) : asNumber(publishedRaw);
  const sources = normalizeSources(value.sources);

  return {
    id,
    title,
    summary: asNullableString(value.summary ?? value.localized_summary ?? localization.summary),
    contentType: asString(value.contentType ?? value.content_type, "news"),
    eventType: asString(value.eventType ?? value.event_type ?? value.contentType ?? value.content_type, "news"),
    locale: asString(value.locale ?? localization.locale, "zh-Hans"),
    publishedAt: Number.isFinite(publishedAt) ? publishedAt : 0,
    sourceCount: asNumber(value.sourceCount ?? value.source_count, sources.length || 1),
    canonicalUrl: asNullableString(value.canonicalUrl ?? value.canonical_url ?? value.primary_url ?? value.url),
    sources,
    platforms: asStringList(value.platforms),
    tags: asStringList(value.tags),
    games: asStringList(value.games ?? value.entities),
  };
}

async function getJson(path: string, signal?: AbortSignal, errorPrefix = "Request failed"): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    throw new Error(`${errorPrefix} (${response.status})`);
  }

  return response.json();
}

export async function getFeed(params: URLSearchParams, signal?: AbortSignal): Promise<FeedResponse> {
  const errorPrefix = params.get("locale") === "zh-Hans" ? "API 请求失败" : "API request failed";
  const payload = await getJson(`/api/v1/feed?${params.toString()}`, signal, errorPrefix);
  const root = isRecord(payload) ? payload : {};
  const rawItems = Array.isArray(root.items)
    ? root.items
    : Array.isArray(root.data)
      ? root.data
      : isRecord(root.data) && Array.isArray(root.data.items)
        ? root.data.items
        : [];
  const meta = isRecord(root.meta) ? root.meta : isRecord(root.data) && isRecord(root.data.meta) ? root.data.meta : {};

  return {
    items: rawItems.map(normalizeEvent).filter((item): item is FeedEvent => item !== null),
    nextCursor: asNullableString(root.nextCursor ?? root.next_cursor ?? meta.nextCursor ?? meta.next_cursor),
  };
}

function normalizeFacetGroup(value: unknown): Array<{ id: string; name: string; count: number }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item === "string") return [{ id: item, name: item, count: 0 }];
    if (!isRecord(item)) return [];
    const name = asString(item.name ?? item.label ?? item.value ?? item.id ?? item.slug);
    if (!name) return [];
    return [{ id: asString(item.id ?? item.slug ?? item.value, name), name, count: asNumber(item.count ?? item.event_count) }];
  });
}

export async function getFacets(signal?: AbortSignal): Promise<Facets> {
  const payload = await getJson("/api/v1/facets", signal);
  const root = isRecord(payload) && isRecord(payload.data) ? payload.data : isRecord(payload) ? payload : {};
  return {
    platforms: normalizeFacetGroup(root.platforms),
    contentTypes: normalizeFacetGroup(root.contentTypes ?? root.content_types),
    tags: normalizeFacetGroup(root.tags),
  };
}

export async function getSources(signal?: AbortSignal): Promise<SourceHealth[]> {
  const payload = await getJson("/api/v1/sources", signal);
  const root = isRecord(payload) ? payload : {};
  const data = isRecord(root.data) ? root.data : {};
  const rawItems = Array.isArray(root.items)
    ? root.items
    : Array.isArray(root.data)
      ? root.data
      : Array.isArray(data.items)
        ? data.items
        : [];
  return rawItems.flatMap((item) => {
    if (!isRecord(item)) return [];
    const id = asString(item.id);
    const name = asString(item.name);
    if (!id || !name) return [];
    return [{
      id,
      name,
      kind: asString(item.kind ?? item.source_type, "media"),
      homepageUrl: asNullableString(item.homepageUrl ?? item.homepage_url ?? item.website_url),
      lastSuccessAt: typeof (item.lastSuccessAt ?? item.last_success_at) === "number"
        ? asNumber(item.lastSuccessAt ?? item.last_success_at)
        : null,
      enabled: item.enabled !== false && item.is_enabled !== 0 && item.is_active !== false,
      itemCount: asNumber(item.itemCount ?? item.item_count ?? item.source_item_count),
    }];
  });
}

function normalizeAiProposalDecision(value: unknown): AiProposalDecision | null {
  return value === "publish" || value === "discard" || value === "review" ? value : null;
}

function normalizeAiProposalAction(value: unknown, decision: AiProposalDecision): AiProposalAction {
  if (value === "create" || value === "skip" || value === "needs_review") return value;
  if (decision === "publish") return "create";
  if (decision === "discard") return "skip";
  return "needs_review";
}

function normalizeAiProposalSource(value: unknown): AiProposalSource | null {
  if (!isRecord(value)) return null;
  const title = asString(value.title).trim();
  if (!title) return null;
  return {
    name: asString(value.name, "未知来源"),
    type: asString(value.type, "other"),
    title,
    url: asString(value.url),
    language: asString(value.language, "und"),
    publishedAt: asNumber(value.published_at),
    isOfficial: asBoolean(value.is_official),
  };
}

function normalizeAiProposalSelection(value: unknown): AiProposalSelection | null {
  if (!isRecord(value)) return null;
  const scoringVersion = asString(value.scoring_version).trim();
  const selectionRunId = asString(value.selection_run_id).trim();
  if (!scoringVersion || !selectionRunId) return null;
  return {
    scoreBps: clamp(asNumber(value.score_bps), 0, 10_000),
    thresholdBps: clamp(asNumber(value.threshold_bps), 0, 10_000),
    scoringVersion,
    sourceTier: asString(value.source_tier, "unknown"),
    freshnessBucket: asString(value.freshness_bucket, "unknown"),
    reasonCodes: asStringList(value.reason_codes),
    matchedEntities: asStringList(value.matched_entities),
    selectionRunId,
  };
}

function normalizeAiFlashNewsTag(value: unknown): AiFlashNewsTag | null {
  if (!isRecord(value)) return null;
  const tagValue = asString(value.value).trim();
  if (!tagValue) return null;
  return {
    kind: asString(value.kind, "topic"),
    value: tagValue,
    confidence: clamp(asNumber(value.confidence), 0, 1),
  };
}

function normalizeAiFlashNewsReview(value: unknown): AiFlashNewsReview | null {
  if (!isRecord(value)) return null;
  const publication = asString(value.publication).trim();
  if (!publication) return null;
  return {
    publication,
    score: asNullableNumber(value.score),
    maxScore: asNullableNumber(value.max_score),
    verdict: asNullableString(value.verdict),
  };
}

function normalizeAiFlashNewsDiscount(value: unknown): AiFlashNewsDiscount | null {
  if (!isRecord(value)) return null;
  const store = asString(value.store).trim();
  if (!store) return null;
  return {
    store,
    currency: asNullableString(value.currency),
    originalPrice: asNullableNumber(value.original_price),
    salePrice: asNullableNumber(value.sale_price),
    endsAt: asNullableString(value.ends_at),
  };
}

function normalizeAiFlashNewsEvent(value: unknown): AiFlashNewsEvent | null {
  if (!isRecord(value)) return null;
  const title = asString(value.title).trim();
  if (!title) return null;
  return {
    eventType: asString(value.event_type, "other"),
    title,
    summary: asString(value.summary),
    language: asString(value.language, "und"),
    occurredAt: asNullableString(value.occurred_at),
    publishedAt: asNullableString(value.published_at),
    confidence: clamp(asNumber(value.confidence), 0, 1),
    isRumor: asBoolean(value.is_rumor),
    games: asStringList(value.games),
    platforms: asStringList(value.platforms),
    version: asNullableString(value.version),
    tags: Array.isArray(value.tags)
      ? value.tags.map(normalizeAiFlashNewsTag).filter((item): item is AiFlashNewsTag => item !== null)
      : [],
    review: normalizeAiFlashNewsReview(value.review),
    discount: normalizeAiFlashNewsDiscount(value.discount),
    // Canonical extraction never carries translations. A later translation
    // feature will use its own response contract and version lifecycle.
    localizations: [],
  };
}

function normalizeAiProposal(value: unknown, requestedLocale: DisplayLocale): AiProposalPreview | null {
  if (!isRecord(value)) return null;
  const id = asString(value.id).trim();
  const decision = normalizeAiProposalDecision(value.decision);
  const source = normalizeAiProposalSource(value.source);
  const selection = normalizeAiProposalSelection(value.selection);
  if (!id || !decision || !source || !selection) return null;

  return {
    id,
    action: normalizeAiProposalAction(value.action, decision),
    confidenceScoreBps: asNullableNumber(value.confidence_score_bps),
    createdAt: asNumber(value.created_at),
    source,
    decision,
    decisionReason: asString(value.decision_reason),
    events: Array.isArray(value.events)
      ? value.events
        .map(normalizeAiFlashNewsEvent)
        .filter((item): item is AiFlashNewsEvent => item !== null)
      : [],
    displayLocale: value.display_locale === "zh-Hans" ? "zh-Hans" : "en",
    translationStatus: value.translation_status === "translated" || value.translation_status === "fallback"
      ? value.translation_status
      : requestedLocale === "zh-Hans"
        ? "fallback"
        : "canonical",
    selection,
  };
}

export async function getAiProposals(locale: DisplayLocale, signal?: AbortSignal): Promise<AiProposalResponse> {
  const params = new URLSearchParams({ limit: "50", locale });
  const errorPrefix = locale === "zh-Hans" ? "API 请求失败" : "API request failed";
  const payload = await getJson(`/api/v1/ai/proposals?${params.toString()}`, signal, errorPrefix);
  const root = isRecord(payload) ? payload : {};
  const data = isRecord(root.data) ? root.data : {};
  const meta = isRecord(root.meta) ? root.meta : {};
  const rawItems = Array.isArray(data.items) ? data.items : [];
  const items = rawItems
    .map((item) => normalizeAiProposal(item, locale))
    .filter((item): item is AiProposalPreview => item !== null);
  const computedSummary = items.reduce(
    (summary, item) => ({ ...summary, [item.decision]: summary[item.decision] + 1 }),
    { publish: 0, discard: 0, review: 0 },
  );
  const rawSummary = isRecord(meta.summary) ? meta.summary : {};

  return {
    items,
    requestId: asString(meta.request_id),
    count: asNumber(meta.count, items.length),
    summary: {
      publish: asNumber(rawSummary.publish, computedSummary.publish),
      discard: asNumber(rawSummary.discard, computedSummary.discard),
      review: asNumber(rawSummary.review, computedSummary.review),
    },
    requestedLocale: meta.requested_locale === "zh-Hans" ? "zh-Hans" : "en",
    canonicalLanguage: "en",
    translatedCount: asNumber(meta.translated_count),
    fallbackCount: asNumber(meta.fallback_count),
  };
}

export { API_BASE_URL };
